/**
 * Render a value that changes faster than the screen does, once per animation frame (the story feed's
 * progressive-narration checkbox: *"buffer to ~1 frame per animation tick rather than
 * re-rendering per token"*).
 *
 * The problem is specific to how the turn stream is shaped. `narration.delta` events arrive in
 * fragments as the model emits them — hundreds over a ~20-second narration phase, in bursts —
 * and each one writes to the Zustand store, so each one is a state change. Without a buffer
 * that is one React render, one reconciliation and one layout per token, all to add three
 * words to a paragraph the reader is still on the first line of. With it, it is at most one
 * per frame, and no reader can tell the difference because a frame is the smallest thing a
 * screen can show.
 *
 * **This is not a typewriter effect and must not become one.** Nothing here delays text: the
 * flush always publishes the *latest* value, so a burst of twenty deltas paints once with all
 * twenty. Metering text out artificially would put the client's animation in charge of a
 * fifty-second wait the player is already impatient with, and it is why this hook has nothing
 * for `prefers-reduced-motion` to turn off (the blinking cursor is the part that moves, and
 * that is CSS — `index.css` neutralizes it globally).
 */
import { useEffect, useRef, useState } from 'react';

/** jsdom without `pretendToBeVisual`, and any environment mid-teardown, have no rAF. */
const schedule: (callback: () => void) => number =
  typeof requestAnimationFrame === 'function'
    ? (callback) => requestAnimationFrame(callback)
    : (callback) => setTimeout(callback, 16) as unknown as number;

const unschedule: (handle: number) => void =
  typeof cancelAnimationFrame === 'function'
    ? (handle) => {
        cancelAnimationFrame(handle);
      }
    : (handle) => {
        clearTimeout(handle);
      };

export function useFrameBuffer<T>(value: T): T {
  const [shown, setShown] = useState(value);
  const latest = useRef(value);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    latest.current = value;
    // A flush is already booked for this frame and it will publish whatever is current when
    // it runs — booking a second one would be the per-token render this hook exists to avoid.
    if (frame.current !== null) return;

    frame.current = schedule(() => {
      frame.current = null;
      setShown(latest.current);
    });
  }, [value]);

  useEffect(
    () => () => {
      if (frame.current === null) return;
      unschedule(frame.current);
      /**
       * **Clearing the handle is the load-bearing half of this line**, and leaving it out cost
       * the whole feature in development.
       *
       * `StrictMode` (`main.tsx`) runs mount → cleanup → mount on the same refs, so a cleanup
       * that cancels the booked frame without forgetting it leaves `frame.current` holding a
       * handle whose callback will never run. Every delta after that takes the "a flush is
       * already booked" early return above, forever: the store fills with narration, the
       * announcer speaks it, and the live scene renders an empty paragraph and a cursor until
       * the terminal `chapter` event replaces the whole thing at once.
       *
       * Invisible to this file's own unit tests, which render without `StrictMode`, and
       * invisible in production, where the double-invoke does not happen — so it was `pnpm dev`
       * and the Playwright suite that had it, which is exactly the pair of environments the
       * fake DM exists to serve. `tests/e2e/feed.spec.ts` is where it was caught.
       */
      frame.current = null;
    },
    [],
  );

  return shown;
}
